import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Col, Row } from "react-bootstrap";
import { GripVertical, Flame, BarChart2, TrendingUp, CalendarClock, ClipboardList, ClipboardCheck, PackageCheck, FileText, Tag, RotateCcw } from "lucide-react";

import _ from "lodash";

import classes from "./dashboard.module.scss";
import { baseFilter, baseValueFilter } from "./dashboard.constants";

import DashboardValorization from "./Cards/Valorization/DashboardValorization";
import DashboardUsedBC from "./Cards/UsedBusinessCenter/DashboardUsedBC";
import DashboardPrices from "./Cards/Prices/DashboardPrices";
import DashboardSales from "./Cards/Sales/DashboardSales";
import DashboardExpiration from "./Cards/Expiration/DashboardExpiration";
import DashboardOut from "./Cards/Out/DashboardOut";
import DashboardLast from "./Cards/Last/DashboardLast";
import DashboardSalesDealer from "./Cards/SalesDealer/DashboardSalesDealer";
import DashboardNotes from "./Cards/Notes/DashboardNotes";
import DashboardRotate from "./Cards/Rotate/DashboardRotate";
import DashboardOA from "@/pages/Dashboard/Cards/OA/DashboardOA.jsx";
import DashboardUpdate from "./DashboardUpdate";
import DashboardMainFilter from "./DashboardMainFilter";
import FastAccess from "./FastAccess/FastAccess";
import FilterShow from "./FilterShow/FilterShow";

import { UPDATE_DASHBOARD, UPDATE_DASHBOARD_FILTER } from "@/redux/types/auth.types";

import apiService from "@/services/api.service";
import { validRoles } from "@/services/Utils";
import { getDashboardFilters } from "@/api/Dashboard/dashboard";
import LoadInventory from "../Inventory/LoadInventory/LoadInventory";

const iconMap = {
	valorization: TrendingUp,
	expiration: CalendarClock,
	sales: BarChart2,
	out: ClipboardList,
	last: ClipboardCheck,
	sales_dealer: PackageCheck,
	used_business_center: Flame,
	notes: FileText,
	prices: Tag,
	rotate: RotateCcw,
	application_orders: FileText,
};

const Dashboard = () => {
	const dispatch = useDispatch();

	const { dashboard: mainDashboard, business_id, aptitudes_id, initial_date, final_date, season } = useSelector((state) => state.auth);
	const { estate_parent_id, estate_id } = useSelector((state) => state.auth.filter);
	const { customer } = useSelector((state) => state.auth.user);

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "administrador_campo", "gerente_agricola"]);
	const isUser = validRoles(userRoles, ["administrador_campo"]);

	const components = {
		valorization: DashboardValorization,
		expiration: DashboardExpiration,
		sales: DashboardSales,
		out: DashboardOut,
		last: DashboardLast,
		sales_dealer: DashboardSalesDealer,
		used_business_center: DashboardUsedBC,
		notes: DashboardNotes,
		prices: DashboardPrices,
		rotate: DashboardRotate,
		application_orders: DashboardOA,
	};

	const { is_implementation_completed } = localStorage.getItem("imp_state")
		? JSON.parse(localStorage.getItem("imp_state"))
		: { implementation_state_id: 2, is_implementation_completed: false };

	const { data, isLoading } = useQuery(["dashboard-filters-" + customer?.id], () => getDashboardFilters({ estate_parent_id, estate_id }), {
		enabled: !!is_implementation_completed,
	});

	const [first, setFirst] = useState([]);
	const [second, setSecond] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [showModalText, setShowModalText] = useState("");
	const [showFilter, setShowFilter] = useState(false);
	const [checked, setChecked] = useState(false);
	const [filters, setFilters] = useState({ ...baseFilter });
	const [manualFilterApplied, setManualFilterApplied] = useState(false);
	const [valueFilters, setValueFilters] = useState({
		...baseValueFilter,
	});

	useEffect(() => {
		let newFilters = { ...filters };
		let newValueFilters = { ...valueFilters };

		if (season && !manualFilterApplied) {
			let newSeason = { ...season };
			newSeason.label = newSeason.name || newSeason.label;
			newSeason.value = newSeason.id || newSeason.value;

			newValueFilters.season_id = newSeason.value;
			newValueFilters.initial_date = newSeason.initial_date;
			newValueFilters.final_date = newSeason.end_date;

			newFilters.season_id.value = newSeason.value;
			newFilters.season_id.label = newSeason.label;

			newFilters.initial_date.value = newSeason.initial_date;
			newFilters.initial_date.label = newSeason.initial_date;

			newFilters.final_date.value = newSeason.end_date;
			newFilters.final_date.label = newSeason.end_date;
		}

		if (business_id?.value) {
			newFilters.business_id = business_id;
			newValueFilters.business_id = business_id.value;
		}
		if (aptitudes_id?.value) {
			newFilters.aptitudes_id = aptitudes_id;
			newValueFilters.aptitudes_id = aptitudes_id.value;
		}
		if (initial_date?.value && !season) {
			newFilters.initial_date = initial_date;
			newValueFilters.initial_date = initial_date.value;
		}
		if (final_date?.value && !season) {
			newFilters.final_date = final_date;
			newValueFilters.final_date = final_date.value;
		}

		setFilters(newFilters);
		setValueFilters(newValueFilters);

		if (is_implementation_completed) {
			let newMainDashboard = [...mainDashboard];
			if (isUser && !isManager) {
				newMainDashboard = newMainDashboard.filter((md) => Number(md.id) !== 10);
			}

			setFirst(
				_.orderBy(
					newMainDashboard.filter((db) => db.active && db.show_column === "first"),
					["show_order"],
					["asc"],
				),
			);
			setSecond(
				_.orderBy(
					newMainDashboard.filter((db) => db.active && db.show_column === "second"),
					["show_order"],
					["asc"],
				),
			);

			if (!checked) {
				apiService.get("/admin/check_updates").then((response) => {
					setChecked(true);
					if (!response.updated) {
						setShowModal(true);
						setShowModalText(response.updated_title);
					}
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [season, mainDashboard, business_id, aptitudes_id, initial_date, final_date, is_implementation_completed]);

	const moveContent = (result) => {
		const { source, destination } = result;

		if (!destination) {
			return;
		}

		if (source.index === destination.index && source.droppableId === destination.droppableId) {
			return;
		}

		let new_first = [...first];
		let new_second = [...second];

		let source_element = null;

		if (source.droppableId === "first") {
			source_element = new_first.splice(source.index, 1)[0];
		}
		if (source.droppableId === "second") {
			source_element = new_second.splice(source.index, 1)[0];
		}

		if (destination.droppableId === "first") {
			source_element.show_column = "first";
			source_element.show_order = destination.index;
			new_first.splice(destination.index, 0, source_element);
		}
		if (destination.droppableId === "second") {
			source_element.show_column = "second";
			source_element.show_order = destination.index;
			new_second.splice(destination.index, 0, source_element);
		}

		const newDashboard = mainDashboard.map((mainElement) => {
			new_first.forEach((firstElement) => {
				if (mainElement.id === firstElement.id) {
					mainElement.show_column = "first";
					mainElement.show_order = firstElement.show_order;
				}
			});
			new_second.forEach((secondElement) => {
				if (mainElement.id === secondElement.id) {
					mainElement.show_column = "second";
					mainElement.show_order = secondElement.show_order;
				}
			});

			return mainElement;
		});

		setFirst(new_first);
		setSecond(new_second);

		dispatch({
			type: UPDATE_DASHBOARD,
			payload: {
				dashboard: newDashboard,
			},
		});

		apiService.post("/client/dashboard/change_order", {
			source_element,
			destination,
		});
	};

	const applyFilter = (params) => {
		setManualFilterApplied(true);

		let newFilter = { ...filters };
		let newValueFilter = { ...valueFilters };

		if (params.businessFilter !== newFilter.business_id) {
			if (params.businessFilter === "") {
				newFilter.business_id.value = "";
				newFilter.business_id.label = "";
			} else {
				newFilter.business_id = params.businessFilter;
				newValueFilter.business_id = params.businessFilter.value;
			}
		}
		if (params.aptitudesFilter !== newFilter.aptitudes_id) {
			if (params.aptitudesFilter === "") {
				newFilter.aptitudes_id.value = "";
				newFilter.aptitudes_id.label = "";
			} else {
				newFilter.aptitudes_id = params.aptitudesFilter;
				newValueFilter.aptitudes_id = params.aptitudesFilter.value;
			}
		}
		if (params.seasonFilter !== newFilter.season_id) {
			if (params.seasonFilter === "") {
				newFilter.season_id.value = "";
				newFilter.season_id.label = "";
			} else {
				newFilter.season_id = params.seasonFilter;
				newValueFilter.season_id = params.seasonFilter.value;
			}
		}
		if (params.initialDateFilter !== newFilter.initial_date) {
			if (params.initialDateFilter === "") {
				newFilter.initial_date.value = "";
				newFilter.initial_date.label = "";
			} else {
				newFilter.initial_date = params.initialDateFilter;
				newValueFilter.initial_date = params.initialDateFilter.value;
			}
		}
		if (params.finalDateFilter !== newFilter.final_date) {
			if (params.finalDateFilter === "") {
				newFilter.final_date.value = "";
				newFilter.final_date.label = "";
			} else {
				newFilter.final_date = params.finalDateFilter;
				newValueFilter.final_date = params.finalDateFilter.value;
			}
		}

		dispatch({
			type: UPDATE_DASHBOARD_FILTER,
			payload: {
				business_id: newFilter.business_id,
				aptitudes_id: newFilter.aptitudes_id,
				season_id: newFilter.season_id,
				initial_date: newFilter.initial_date,
				final_date: newFilter.final_date,
			},
		});

		setFilters(newFilter);
		setValueFilters(newValueFilter);
		setShowFilter(false);
	};

	return (
		<>
			{is_implementation_completed ? (
				<>
					<DragDropContext onDragEnd={(result) => moveContent(result)}>
						<div id="printable-area" className={classes.dashboard}>
							<FastAccess showFilter={showFilter} setShowFilter={setShowFilter} />
							<FilterShow filters={filters} valueFilters={valueFilters} setFilters={setFilters} setValueFilters={setValueFilters} />

							<Row className={classes.dashboard_row}>
								{showFilter && <DashboardMainFilter data={data} isLoading={isLoading} applyFilter={applyFilter} filters={filters} />}

								<Col className={classes.dashboard_col} md={showFilter ? 9 : 12}>
									<div className={classes.dashboard_column}>
										<Droppable droppableId="first">
											{(droppableProvided) => (
												<div
													className={classes.dashboard_content}
													{...droppableProvided.droppableProps}
													ref={droppableProvided.innerRef}
												>
													{first.map((option, i) => {
														let Component = components[option.description];
														const Icon = iconMap[option.description] || BarChart2;

														if (!Component) return null;

														return (
															<Draggable key={option.id} index={i} draggableId={String(option.id)}>
																{(draggableProvided) => (
																	<div
																		{...draggableProvided.draggableProps}
																		ref={draggableProvided.innerRef}
																		className={classes.dashboard_content_item}
																	>
																		<div className={classes.container}>
																			<div className={classes.cardHeader}>
																				<Icon size={16} className={classes.iconTitle} />
																				<span className={classes.cardTitle}>{option.name}</span>
																				<span {...draggableProvided.dragHandleProps} className={classes.dragIcon}>
																					<GripVertical size={16} />
																				</span>
																			</div>

																			{option.description === "prices" ? (
																				<Component filters={valueFilters} products={data ? data.products : []} />
																			) : (
																				<Component filters={valueFilters} />
																			)}
																		</div>
																	</div>
																)}
															</Draggable>
														);
													})}
													{droppableProvided.placeholder}
												</div>
											)}
										</Droppable>
									</div>

									<div className={classes.dashboard_column}>
										<Droppable droppableId="second">
											{(droppableProvided) => (
												<div
													className={classes.dashboard_content}
													{...droppableProvided.droppableProps}
													ref={droppableProvided.innerRef}
												>
													{second.map((option, i) => {
														let Component = components[option.description];
														const Icon = iconMap[option.description] || BarChart2;

														if (!Component) return null;

														return (
															<Draggable key={option.id} index={i} draggableId={String(option.id)}>
																{(draggableProvided) => (
																	<div
																		{...draggableProvided.draggableProps}
																		ref={draggableProvided.innerRef}
																		className={classes.dashboard_content_item}
																	>
																		<div className={classes.container}>
																			<div className={classes.cardHeader}>
																				<Icon size={16} className={classes.iconTitle} />
																				<span className={classes.cardTitle}>{option.name}</span>
																				<span {...draggableProvided.dragHandleProps} className={classes.dragIcon}>
																					<GripVertical size={16} />
																				</span>
																			</div>

																			{option.description === "prices" ? (
																				<Component filters={valueFilters} products={data ? data.products : []} />
																			) : (
																				<Component filters={valueFilters} />
																			)}
																		</div>
																	</div>
																)}
															</Draggable>
														);
													})}
													{droppableProvided.placeholder}
												</div>
											)}
										</Droppable>
									</div>
								</Col>
							</Row>
						</div>
					</DragDropContext>

					<DashboardUpdate
						message="Hay una nueva actualización disponible."
						show={showModal}
						showModalText={showModalText}
						handleClose={() => setShowModal(false)}
					></DashboardUpdate>
				</>
			) : (
				<LoadInventory />
			)}
		</>
	);
};

export default Dashboard;
